import React, { useState, useMemo, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";

const PREFIJOS_EXCLUIDOS = ['DSG', 'DSF', 'DVG', 'DSGC', 'CM', 'CMSJ', 'CMXB', 'CMXC', 'CMXD', 'CMXE', 'CMXF', 'CMBL', 'CMAC', 'CMLG','CMGC', 'POS', 'DVF'];
const PREFIJOS_EXCLUIDOS_SET = new Set(PREFIJOS_EXCLUIDOS.map((prefijo) => String(prefijo).trim().toUpperCase()));
const esPrefijoExcluido = (valor = "") => {
  const valorNormalizado = String(valor).trim().toUpperCase();
  return [...PREFIJOS_EXCLUIDOS_SET].some((prefijo) => valorNormalizado.startsWith(prefijo));
};

export default function CierreMes({ usuario, onBackToMenu }) {
  // -------------------------
  // 1. LÓGICA DE CONTROL ABAC 
  // -------------------------
  const permisosABAC = useMemo(() => {
    try {
      const parsed = typeof usuario?.permisos_avanzados === 'string' 
        ? JSON.parse(usuario.permisos_avanzados) 
        : (usuario?.permisos_avanzados || {});
      return parsed.micros || parsed; 
    } catch (e) { 
      return {}; 
    }
  }, [usuario]);

  const cargoUpper = String(usuario?.cargo || "").toUpperCase().trim();
  const esAdminGlobal = cargoUpper === "SUPER_ADMIN";
  const checkPermiso = (nombreSubmodulo) => {
    if (esAdminGlobal) return true;
    return permisosABAC[nombreSubmodulo] !== undefined;
  };

  const tienePermisoAuditoria = checkPermiso('Auditoría y Cruce');
  const tienePermisoSysplus = checkPermiso('Datos Brutos Sysplus');
  const tienePermisoDian = checkPermiso('Subir Archivo DIAN');

  // --------------------------------------------------
  // 2. ESTADOS
  // --------------------------------------------------
  const [fechaInicial, setFechaInicial] = useState("");
  const [fechaFinal, setFechaFinal] = useState("");
  
  useEffect(() => {
    const hoy = new Date();
    const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);

    const formatFecha = (fecha) => {
      const yyyy = fecha.getFullYear();
      const mm = String(fecha.getMonth() + 1).padStart(2, '0');
      const dd = String(fecha.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    setFechaInicial(formatFecha(primerDia));
    setFechaFinal(formatFecha(ultimoDia));
  }, []);

  const [procesando, setProcesando] = useState(false);
  const [mensaje, setMensaje] = useState({ text: "", type: "" });
  
  const [estadoCierreV2, setEstadoCierreV2] = useState("ABIERTO");
  const [cargandoCierre, setCargandoCierre] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState("");
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [facturasGuardadas, setFacturasGuardadas] = useState([]); 

  const [sysplusExtraido, setSysplusExtraido] = useState(false);
  const [comprasSysplusCache, setComprasSysplusCache] = useState([]);

  const [filtroEstado, setFiltroEstado] = useState("TODOS");
  const [busqueda, setBusqueda] = useState(""); 
  
  const [filtroTipos, setFiltroTipos] = useState(["FE", "NC", "ND"]);
  const [mostrarMenuTipos, setMostrarMenuTipos] = useState(false);

  const [vistaActual, setVistaActual] = useState("CRUCE");
  const [sortCruce, setSortCruce] = useState({ key: null, direction: 'asc' });

  const [paginaActual, setPaginaActual] = useState(1);
  const itemsPorPagina = 100;

  const [modalAuditoria, setModalAuditoria] = useState({ isOpen: false, item: null, estado: 'PENDIENTE', nuevaObservacion: '' });
  const [modalConfirmarLimpiar, setModalConfirmarLimpiar] = useState(false);

  const firmaUsuario = `${usuario?.nombres || ''} ${usuario?.apellidos || ''} - ${usuario?.cargo || 'Admin'}`;
  const firmaCortada = `${usuario?.nombres || ''} ${usuario?.apellidos || ''}`;

  const limpiarTexto = (texto) => texto ? String(texto).replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').trim() : "";
  const getPeriodo = (fechaStr) => {
    if (!fechaStr) return { anio: null, mes: null };
    const partes = fechaStr.split("-");
    return { anio: partes[0], mes: parseInt(partes[1], 10) };
  };

  const normalizarTipo = (tipoStr, folioSys = "", folioDian = "") => {
    const t = String(tipoStr || "").toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const fS = String(folioSys || "").toUpperCase();
    const fD = String(folioDian || "").toUpperCase();
    const prefijoExcluido = esPrefijoExcluido(fS) || esPrefijoExcluido(fD);

    if (prefijoExcluido) return "FE";
    if (t.includes("NC") || t.includes("CREDITO") || fS.includes("NC") || fD.includes("NC") || fS.includes("DEVOL")) return "NC";
    if (t.includes("ND") || t.includes("DEBITO") || fS.includes("ND") || fD.includes("ND")) return "ND";
    return "FE";
  };

  const formatTipoDoc = (tipoStr) => {
    const t = normalizarTipo(tipoStr);
    if (t === "NC") return { label: "Nota Crédito", color: "bg-orange-100 text-orange-800 border-orange-200" };
    if (t === "ND") return { label: "Nota Débito", color: "bg-rose-100 text-rose-800 border-rose-200" };
    return { label: "Factura (FE)", color: "bg-blue-100 text-blue-800 border-blue-200" };
  };

  // --------------------------------------------------
  // 3. CONSULTAR DB AL CAMBIAR FECHAS
  // --------------------------------------------------
  const cargarDatosDelMes = useCallback(async () => {
    if (!fechaInicial) return;
    setCargandoHistorial(true);
    try {
      const { anio, mes } = getPeriodo(fechaInicial);
      const resEst = await fetch(`${process.env.REACT_APP_API_URL}/api/dian/cierre-estado?anio=${anio}&mes=${mes}`);
      const dataEst = await resEst.json();
      if (dataEst.success) setEstadoCierreV2(dataEst.estado || "ABIERTO");

      const resHist = await fetch(`${process.env.REACT_APP_API_URL}/api/dian/cierre-historial?anio=${anio}&mes=${mes}`);
      const dataHist = await resHist.json();
      if (dataHist.success) setHistorial(dataHist.historial || []);

      const resFac = await fetch(`${process.env.REACT_APP_API_URL}/api/dian/cruce-facturas?anio=${anio}&mes=${mes}`);
      const dataFac = await resFac.json();
      if (dataFac.success) {
        setFacturasGuardadas(dataFac.facturas || []);
        setPaginaActual(1);
      }
    } catch (err) {
      console.error("Error cargando datos del mes:", err);
    } finally {
      setCargandoHistorial(false);
    }
  }, [fechaInicial]);

  useEffect(() => { cargarDatosDelMes(); }, [cargarDatosDelMes]);

  // --------------------------------------------------
  // 4. ACCIONES Y VALIDACIONES
  // --------------------------------------------------
  const actualizarEstadoCierreV2 = async (nuevoEstado) => {
    if (!fechaInicial) return;
    setCargandoCierre(true);
    try {
      const { anio, mes } = getPeriodo(fechaInicial);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/dian/cierre-estado`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ anio, mes, estado: nuevoEstado, usuario: firmaUsuario })
      });
      if ((await res.json()).success) {
        setEstadoCierreV2(nuevoEstado);
        cargarDatosDelMes(); 
        setMensaje({ text: `🔒 Periodo actualizado a: ${nuevoEstado}`, type: "success" });
      }
    } catch (err) { setMensaje({ text: "Error de conexión con el servidor.", type: "error" }); } 
    finally { setCargandoCierre(false); }
  };

  const ejecutarLimpiarPeriodo = async () => {
    setModalConfirmarLimpiar(false);
    const { anio, mes } = getPeriodo(fechaInicial);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/dian/cruce-facturas?anio=${anio}&mes=${mes}`, { method: "DELETE" });
      if ((await res.json()).success) {
        setFacturasGuardadas([]);
        setSysplusExtraido(false);
        setComprasSysplusCache([]);
        cargarDatosDelMes();
        setMensaje({ text: "🗑️ Se ha reiniciado el periodo correctamente.", type: "warning" });
      }
    } catch (err) { setMensaje({ text: "Error al limpiar el periodo.", type: "error" }); }
  };

  const handleAgregarComentario = async () => {
    if (!nuevoComentario.trim() || !fechaInicial) return;
    try {
      const { anio, mes } = getPeriodo(fechaInicial);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/dian/cierre-historial`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ anio, mes, usuario: `${firmaUsuario} (CC: ${usuario?.documento || 'N/A'})`, detalle: nuevoComentario })
      });
      if ((await res.json()).success) {
        setNuevoComentario("");
        cargarDatosDelMes();
        setMensaje({ text: "✅ Novedad registrada en bitácora.", type: "success" });
      }
    } catch (err) { setMensaje({ text: "Error al guardar comentario.", type: "error" }); }
  };

  const guardarAuditoriaFactura = async () => {
    const { item, estado, nuevaObservacion } = modalAuditoria;
    let observacionFinal = item.observacion || "";
    if (nuevaObservacion.trim() !== "") {
      const timestamp = new Date().toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
      const nuevaEntrada = `🔵 [${timestamp}] ${firmaCortada}:\n${nuevaObservacion.trim()}`;
      observacionFinal = observacionFinal ? `${nuevaEntrada}\n\n${observacionFinal}` : nuevaEntrada;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/dian/cruce-facturas/validar`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id, estado_auditoria: estado, observacion: observacionFinal, usuario: firmaCortada })
      });
      if ((await res.json()).success) {
        setFacturasGuardadas(prev => prev.map(f => f.id === item.id ? { ...f, estado_auditoria: estado, observacion: observacionFinal, usuario_validador: firmaCortada } : f));
        setModalAuditoria({ isOpen: false, item: null, estado: 'PENDIENTE', nuevaObservacion: '' });
      }
    } catch (error) { setMensaje({ text: "Error al actualizar la factura.", type: "error" }); }
  };

  const validarConciliadosMasivamente = async () => {
    if (estadoCierreV2 === "CERRADO") return;
    const facturasParaValidar = facturasGuardadas.filter(f => f.estado_cruce === "CONCILIADO" && f.estado_auditoria !== "VALIDADO");
    const ids = facturasParaValidar.map(f => f.id);

    if (ids.length === 0) return setMensaje({ text: "No hay facturas conciliadas pendientes por validar.", type: "warning" });

    setProcesando(true);
    setMensaje({ text: `⏳ Validando ${ids.length} facturas automáticamente...`, type: "success" });

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/dian/cruce-facturas/validar-masivo`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids, estado_auditoria: "VALIDADO", usuario: firmaCortada })
      });
      if ((await res.json()).success) {
        cargarDatosDelMes();
        setMensaje({ text: `✅ Se validaron ${ids.length} facturas conciliadas masivamente.`, type: "success" });
      }
    } catch (error) { setMensaje({ text: "Error en la validación masiva.", type: "error" }); } 
    finally { setProcesando(false); }
  };

  // --------------------------------------------------
  // 5. EXTRACCIÓN Y CRUCE (CON VALIDACIÓN ESTRICTA)
  // --------------------------------------------------
  const consultarSysplus = async () => {
    if(!fechaInicial || !fechaFinal) throw new Error("Debes seleccionar una fecha de inicio y fin válidas.");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/dian/compras-sistema?inicio=${fechaInicial}&fin=${fechaFinal}`);
      const data = await res.json(); 
      if (!res.ok) throw new Error(`Error de Sysplus: ${data.message || 'Tiempo de espera superado.'}`);
      if (data.success) return data.compras;
      throw new Error(data.message);
    } catch (err) { 
      throw new Error(err.message || "Error de conexión con Sysplus."); 
    }
  };

  const handleExtraerSysplusPrimero = async () => {
    if (!fechaInicial || !fechaFinal) return setMensaje({ text: "⚠️ Debes seleccionar la fecha de INICIO y FIN primero.", type: "warning" });
    setProcesando(true);
    setMensaje({ text: "⏳ Extrayendo datos de Sysplus para el rango seleccionado...", type: "success text-gray-800" });

    try {
      const sysData = await consultarSysplus();
      if (!sysData || sysData.length === 0) {
        setMensaje({ text: "⚠️ No se encontraron registros en Sysplus para estas fechas.", type: "warning" });
        setSysplusExtraido(false);
        setComprasSysplusCache([]);
      } else {
        setComprasSysplusCache(sysData);
        setSysplusExtraido(true);
        setMensaje({ text: `✅ ¡Sysplus extraído con éxito (${sysData.length} registros)! Ahora ya puedes subir el Excel de la DIAN.`, type: "success" });
      }
    } catch (err) {
      setMensaje({ text: err.message || "Error al extraer de Sysplus.", type: "error" });
      setSysplusExtraido(false);
      setComprasSysplusCache([]);
    } finally {
      setProcesando(false);
    }
  };

  const handleRefrescarFaltantes = async () => {
    if (!fechaInicial || !fechaFinal) return setMensaje({ text: "Selecciona las fechas del periodo.", type: "warning" });
    
    setProcesando(true);
    setMensaje({ text: "⏳ Buscando facturas faltantes en Sysplus...", type: "success" });
    
    try {
      const sysData = await consultarSysplus();
      let actualizadas = 0;
      const facturasAActualizar = [];
      const cleanFolio = (str) => String(str || "").replace(/^0+/, "").replace(/[\s-]/g, "").toUpperCase();

      facturasGuardadas.forEach(fac => {
        if (fac.estado_cruce === "FALTANTE_SISTEMA") {
          const nitNormalizado = String(fac.nit).replace(/\D/g, "");
          const partesFolio = String(fac.folio_dian).split(" ");
          const folioOriginal = partesFolio.length > 1 ? partesFolio[1] : partesFolio[0]; 
          
          const keyBuscada = cleanFolio(folioOriginal);

          const matchSys = sysData.find(s => 
            String(s.nit_proveedor).replace(/\D/g, "") === nitNormalizado && 
            cleanFolio(s.folio_proveedor) === keyBuscada
          );

          if (matchSys) {
            const difTotal = Math.abs(Math.abs(fac.total_dian) - Math.abs(matchSys.total)); 
            const folioVisualSysplus = `${matchSys.prefijo_interno || ""} ${matchSys.folio_interno || ""} (Prov: ${matchSys.folio_proveedor || "N/A"})`.trim();
            
            facturasAActualizar.push({
              nit: nitNormalizado, 
              nombre: matchSys.nombre_proveedor, 
              folioDian: fac.folio_dian, 
              folioSysplus: folioVisualSysplus, 
              tipo: normalizarTipo(matchSys.tipo_doc || fac.tipo_doc, matchSys.prefijo_interno, fac.folio_dian), 
              totalDian: fac.total_dian, 
              totalSysplus: matchSys.total, 
              diferencia: difTotal, 
              estado: difTotal < 50 ? "CONCILIADO" : "DIFERENCIA",
              fechaDian: fac.fecha_dian, 
              fechaSysplus: matchSys.fecha || null 
            });
            actualizadas++;
          }
        }
      });

      if (actualizadas > 0) {
        const { anio, mes } = getPeriodo(fechaInicial);
        const resCruce = await fetch(`${process.env.REACT_APP_API_URL}/api/dian/cruce-facturas`, {
          method: "POST", headers: { "Content-Type": "application/json" }, 
          body: JSON.stringify({ anio, mes, facturas: facturasAActualizar })
        });
        const dataCruce = await resCruce.json();
        
        if (dataCruce.success) {
          cargarDatosDelMes();
          setMensaje({ text: `✅ Se encontraron y actualizaron ${actualizadas} facturas desde Sysplus.`, type: "success" });
        }
      } else {
        setMensaje({ text: "La búsqueda terminó, pero aún no se han digitado las facturas faltantes en Sysplus.", type: "warning" });
      }
    } catch (err) {
      setMensaje({ text: err.message || "Error al sincronizar con Sysplus.", type: "error" });
    } finally {
      setProcesando(false);
    }
  };

  const procesarCruceYGuardar = async (dianData, sysplusData) => {
    const resultados = [];
    const dianMap = new Map();
    const normalizarNIT = (nit) => String(nit || "").split("-")[0].replace(/\D/g, "");
    const cleanFolio = (str) => String(str || "").replace(/^0+/, "").replace(/[\s-]/g, "").toUpperCase();

    // 1. Mapear la DIAN con clave estricta: NIT + Folio Limpio
    dianData.forEach(dian => {
      const nitClean = normalizarNIT(dian.nit);
      const folioClean = cleanFolio(dian.folioOriginal);
      const key = `${nitClean}_${folioClean}`;
      
      if (!dianMap.has(key)) {
        dianMap.set(key, { ...dian, procesado: false });
      }
    });

    // 2. Cruzar con Sysplus exigiendo coincidencia exacta de NIT y Folio del Proveedor
    sysplusData.forEach((sys, index) => {
      const nitSysClean = normalizarNIT(sys.nit_proveedor);
      const folioSysClean = cleanFolio(sys.folio_proveedor);
      const key = `${nitSysClean}_${folioSysClean}`;
      
      const folioVisualSysplus = `${sys.prefijo_interno || ""} ${sys.folio_interno || ""} (Prov: ${sys.folio_proveedor || "N/A"})`.trim();
      let tipoSys = normalizarTipo(sys.tipo_doc, sys.prefijo_interno, "");

      if (dianMap.has(key)) {
        const dianMatch = dianMap.get(key);
        dianMatch.procesado = true;
        const difTotal = Math.abs(Math.abs(dianMatch.total) - Math.abs(sys.total)); 
        
        resultados.push({
          nit: nitSysClean, 
          nombre: sys.nombre_proveedor, 
          folioDian: `${dianMatch.tipo} ${dianMatch.folioOriginal}`, 
          folioSysplus: folioVisualSysplus, 
          tipo: normalizarTipo(dianMatch.tipo, sys.prefijo_interno, dianMatch.folioOriginal), 
          totalDian: dianMatch.total, 
          totalSysplus: sys.total, 
          diferencia: difTotal, 
          estado: difTotal < 50 ? "CONCILIADO" : "DIFERENCIA",
          fechaDian: dianMatch.fechaDian || null, 
          fechaSysplus: sys.fecha || null 
        });
      } else {
        resultados.push({
          nit: nitSysClean, 
          nombre: sys.nombre_proveedor, 
          folioDian: `NO-DIAN-${sys.prefijo_interno || ""}${sys.folio_interno || index}`, 
          folioSysplus: folioVisualSysplus, 
          tipo: tipoSys, 
          totalDian: 0, 
          totalSysplus: Number(sys.total || 0), 
          diferencia: Math.abs(Number(sys.total || 0)), 
          estado: "FALTANTE_DIAN",
          fechaDian: null,
          fechaSysplus: sys.fecha || null
        });
      }
    });

    dianMap.forEach((dianMatch) => {
      if (!dianMatch.procesado) {
        resultados.push({
          nit: normalizarNIT(dianMatch.nit), 
          nombre: dianMatch.nombre, 
          folioDian: `${dianMatch.tipo} ${dianMatch.folioOriginal}`, 
          folioSysplus: "No registrado", 
          tipo: normalizarTipo(dianMatch.tipo), 
          totalDian: dianMatch.total, 
          totalSysplus: 0, 
          diferencia: Math.abs(dianMatch.total), 
          estado: "FALTANTE_SISTEMA",
          fechaDian: dianMatch.fechaDian || null,
          fechaSysplus: null
        });
      }
    });

    if (resultados.length === 0) {
      setMensaje({ text: "No se generó ningún registro de cruce.", type: "warning" });
      return false;
    }

    const { anio, mes } = getPeriodo(fechaInicial);
    try {
      const resCruce = await fetch(`${process.env.REACT_APP_API_URL}/api/dian/cruce-facturas`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ anio, mes, facturas: resultados })
      });
      const dataCruce = await resCruce.json();
      if (!dataCruce.success) throw new Error("Error BD");
      return true;
    } catch (err) {
      setMensaje({ text: "Error guardando el cruce en la base de datos local.", type: "error" });
      return false;
    }
  };

  const handleCargarDian = (e) => {
    if (estadoCierreV2 === "CERRADO") return setMensaje({ text: "🔒 Periodo CERRADO.", type: "error" });
    const file = e.target.files[0];
    if (!file || !fechaInicial || !fechaFinal) return setMensaje({ text: "⚠️ Fechas y archivo son obligatorios.", type: "error" });

    const sysData = comprasSysplusCache;
    if (!sysData || sysData.length === 0) {
      setMensaje({ text: "⚠️ ALERTA: Debes completar el Paso 1 (Extraer Sysplus) antes de subir el Excel DIAN.", type: "error" });
      return;
    }

    setProcesando(true);
    setMensaje({ text: "⏳ Procesando archivo DIAN y cruzando con Sysplus...", type: "success" });

    setTimeout(() => {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const wb = XLSX.read(evt.target.result, { type: "binary", cellDates: true });
          const rawData = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
          
          const procesadosDian = rawData.map((row) => {
            const getColValue = (rowObj, posiblesNombres) => {
              const keys = Object.keys(rowObj);
              for (let nombre of posiblesNombres) {
                const nombreLimpio = nombre.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                const keyEncontrada = keys.find(k => k.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim() === nombreLimpio);
                if (keyEncontrada) return rowObj[keyEncontrada];
              }
              return null;
            };

            const tipoDocOriginal = String(getColValue(row, ["Tipo de documento", "Tipo Documento", "Tipo doc"]) || "").trim().toLowerCase();
            const tipoDoc = tipoDocOriginal.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

            const nitEmisor = String(getColValue(row, ["NIT Emisor", "NIT", "Nit"]) || "").trim();
            const nombreEmisorOriginal = getColValue(row, ["Nombre Emisor", "Emisor", "Proveedor"]) || "Desconocido";
            const nombreEmisorLower = String(nombreEmisorOriginal).toLowerCase();

            if (
              tipoDoc.includes("application response") || 
              tipoDoc.includes("documento equivalente pos") ||
              tipoDoc.includes("cuantia menor") ||
              nombreEmisorLower.includes("cuantias menores") ||
              nitEmisor === "222222222222"
            ) {
              return { valido: false }; 
            }

            let multiplicador = 1;
            let tipoFinal = "FE";
            
            if (tipoDoc.includes("credito") || tipoDoc.includes("nc")) {
              tipoFinal = "NC"; multiplicador = -1;
            } else if (tipoDoc.includes("debito") || tipoDoc.includes("nd")) {
              tipoFinal = "ND";
            }
            
            const prefijo = String(getColValue(row, ["Prefijo"]) || "");
            const folioNumber = String(getColValue(row, ["Folio", "Número", "Numero", "Documento"]) || "");
            const numeroOriginal = prefijo + folioNumber;
            const total = Number(getColValue(row, ["Total", "Valor Total", "Total Factura"]) || 0);

            let fechaDianRaw = getColValue(row, ["Fecha Emisión", "Fecha de emisión", "Fecha Emision", "Fecha de generación", "Fecha Generacion", "Fecha"]);
            let fechaFinalStr = null;

            if (fechaDianRaw) {
              if (fechaDianRaw instanceof Date && !isNaN(fechaDianRaw)) {
                 fechaFinalStr = fechaDianRaw.toISOString().split('T')[0];
              } else if (typeof fechaDianRaw === 'number') {
                 if (fechaDianRaw > 10000) {
                   const fechaJS = new Date(Math.round((fechaDianRaw - 25569) * 86400 * 1000));
                   fechaFinalStr = fechaJS.toISOString().split('T')[0];
                 }
              } else if (typeof fechaDianRaw === 'string' || typeof fechaDianRaw === 'object') {
                 let strLimpio = String(fechaDianRaw).split(' ')[0].trim();
                 
                 if (strLimpio.includes('/') || strLimpio.includes('-')) {
                   const separador = strLimpio.includes('/') ? '/' : '-';
                   const partes = strLimpio.split(separador);
                   
                   if (partes.length === 3) {
                     if (partes[0].length <= 2 && partes[2].length === 4) {
                       fechaFinalStr = `${partes[2]}-${partes[1].padStart(2, '0')}-${partes[0].padStart(2, '0')}`;
                     } 
                     else if (partes[0].length === 4) {
                       fechaFinalStr = `${partes[0]}-${partes[1].padStart(2, '0')}-${partes[2].padStart(2, '0')}`;
                     }
                   }
                 }
                 if (!fechaFinalStr && strLimpio.length >= 8) {
                   fechaFinalStr = strLimpio; 
                 }
              }
            }

            return {
              nit: nitEmisor,
              nombre: limpiarTexto(nombreEmisorOriginal),
              folioOriginal: numeroOriginal, 
              tipo: normalizarTipo(tipoFinal, prefijo, numeroOriginal),
              total: total * multiplicador,
              valido: nitEmisor !== "",
              fechaDian: fechaFinalStr
            };
          }).filter(item => item.valido); 

          const guardadoExitoso = await procesarCruceYGuardar(procesadosDian, sysData);
          
          if (guardadoExitoso) {
            const { anio, mes } = getPeriodo(fechaInicial);
            await fetch(`${process.env.REACT_APP_API_URL}/api/dian/cierre-historial`, {
              method: "POST", headers: { "Content-Type": "application/json" }, 
              body: JSON.stringify({ anio, mes, usuario: firmaUsuario, detalle: "Subió archivo DIAN y completó el cruce automático." })
            });
            cargarDatosDelMes();
            setMensaje({ text: `¡Éxito! Cruce completado, se auto-repararon los documentos y se guardaron las notas.`, type: "success" });
          }
        } catch (err) { 
          setMensaje({ text: err.message || "Error al procesar los archivos.", type: "error" }); 
        } finally { 
          setProcesando(false); 
        }
      };
      reader.readAsBinaryString(file);
    }, 100);
  };

  // --------------------------------------------------
  // 6. PREPARAR DATOS Y PAGINACIÓN
  // --------------------------------------------------
  const datosBase = useMemo(() => {
    return facturasGuardadas.map(f => ({
      id: f.id, 
      nit: f.nit || "", 
      nombre: f.nombre_proveedor || "Desconocido", 
      folioDian: f.folio_dian || "N/A", 
      folioSysplus: f.folio_sysplus || "N/A",
      tipo: normalizarTipo(f.tipo_doc, f.folio_sysplus, f.folio_dian), 
      totalDian: Number(f.total_dian || 0), 
      totalSysplus: Number(f.total_sysplus || 0), 
      diferencia: Number(f.diferencia || 0),
      estado: f.estado_cruce, 
      estado_auditoria: f.estado_auditoria, 
      usuario_validador: f.usuario_validador, 
      observacion: f.observacion || "",
      fechaDian: f.fecha_dian, 
      fechaSysplus: f.fecha_sysplus
    }));
  }, [facturasGuardadas]);

  const datosFiltradosSinEstado = useMemo(() => {
    return datosBase.filter(item => {
      const matchTipo = filtroTipos.includes(item.tipo);
      const searchStr = busqueda.toLowerCase().trim();
      const matchBusqueda = searchStr === "" ||
        String(item.nombre || "").toLowerCase().includes(searchStr) ||
        String(item.nit || "").toLowerCase().includes(searchStr) ||
        String(item.folioDian || "").toLowerCase().includes(searchStr) ||
        String(item.folioSysplus || "").toLowerCase().includes(searchStr);

      return matchTipo && matchBusqueda;
    });
  }, [datosBase, filtroTipos, busqueda]);

  const kpis = useMemo(() => {
    return datosFiltradosSinEstado.reduce((acc, curr) => {
      acc.netoDian += Number(curr.totalDian) || 0; 
      acc.netoSysplus += Number(curr.totalSysplus) || 0;
      if (curr.estado === "CONCILIADO") acc.ok++;
      if (curr.estado === "FALTANTE_SISTEMA") acc.faltaSist++;
      if (curr.estado === "FALTANTE_DIAN") acc.faltaDian++;
      if (curr.estado === "DIFERENCIA") acc.dif++;
      if (curr.estado_auditoria === "VALIDADO") acc.validados++;
      return acc;
    }, { netoDian: 0, netoSysplus: 0, ok: 0, faltaSist: 0, faltaDian: 0, dif: 0, validados: 0, total: datosFiltradosSinEstado.length });
  }, [datosFiltradosSinEstado]);

  const datosFiltrados = useMemo(() => {
    let datos = datosFiltradosSinEstado.filter(item => filtroEstado === "TODOS" || item.estado === filtroEstado);

    if (sortCruce.key) {
      datos.sort((a, b) => {
        let valA = a[sortCruce.key];
        let valB = b[sortCruce.key];
        if (valA === null || valA === undefined) valA = '';
        if (valB === null || valB === undefined) valB = '';

        if (typeof valA === 'string' && typeof valB === 'string') {
          return sortCruce.direction === 'asc'
            ? valA.localeCompare(valB, 'es', { sensitivity: 'base' })
            : valB.localeCompare(valA, 'es', { sensitivity: 'base' });
        }
        if (valA < valB) return sortCruce.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortCruce.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return datos;
  }, [datosFiltradosSinEstado, filtroEstado, sortCruce]);

  const totalPaginas = Math.ceil(datosFiltrados.length / itemsPorPagina);
  const datosPaginados = useMemo(() => {
    const inicio = (paginaActual - 1) * itemsPorPagina;
    return datosFiltrados.slice(inicio, inicio + itemsPorPagina);
  }, [datosFiltrados, paginaActual]);

  const handleSortCruce = (key) => {
    let direction = 'asc';
    if (sortCruce.key === key && sortCruce.direction === 'asc') direction = 'desc';
    setSortCruce({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortCruce.key !== key) return " ↕";
    return sortCruce.direction === 'asc' ? " 🔼" : " 🔽";
  };

  const exportarExcelCierre = async () => {
    if (estadoCierreV2 !== "CERRADO" || datosFiltrados.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(datosFiltrados.map(i => ({
      "Auditoría": i.estado_auditoria, "Observación": i.observacion, "Estado Cruce": i.estado, 
      "Tipo Documento": formatTipoDoc(i.tipo).label,
      "Folio DIAN": i.folioDian.startsWith('NO-DIAN') ? 'N/A' : i.folioDian, 
      "Fecha Generación DIAN": i.fechaDian ? String(i.fechaDian).split('T')[0] : 'N/A', 
      "Folio Sysplus": i.folioSysplus,
      "Fecha Sysplus": i.fechaSysplus ? String(i.fechaSysplus).split('T')[0] : 'N/A',
      "Proveedor": i.nombre, "NIT": i.nit, "Total DIAN": i.totalDian, "Total Sysplus": i.totalSysplus, "Diferencia": i.diferencia
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cierre_Mensual");
    XLSX.writeFile(wb, `Cierre_Auditoria_${fechaInicial}.xlsx`);
  };

  const fechasLlenas = fechaInicial && fechaFinal;

  if (!tienePermisoAuditoria && !tienePermisoSysplus && !tienePermisoDian) {
    return (
      <div className="bg-white/50 backdrop-blur-[45px] rounded-[34px] shadow-[0_25px_70px_rgba(0,0,0,0.15)] p-10 max-w-4xl w-full mx-auto border border-white/80 text-center">
        <span className="text-6xl block mb-6">🛡</span><h2 className="text-2xl font-black text-gray-900 mb-2">Acceso Restringido</h2>
        <button onClick={onBackToMenu} className="px-6 py-2.5 bg-blue-600 text-white font-bold rounded-full">Volver</button>
      </div>
    );
  }

  return (
    <>
      {modalConfirmarLimpiar && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 backdrop-blur-md p-4 animate-fade-in">
          <div className="bg-white rounded-[32px] p-8 shadow-2xl w-full max-w-md border border-white/80 text-center">
            <div className="w-16 h-16 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center text-3xl mx-auto mb-4">🗑️</div>
            <h3 className="text-xl font-black text-slate-900 mb-2">¿Reiniciar Cruce del Periodo?</h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-6">
              Se eliminarán las facturas guardadas en este mes para que puedas volver a extraer de Sysplus y subir el Excel DIAN desde cero.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setModalConfirmarLimpiar(false)} className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold text-xs text-gray-600 cursor-pointer">Cancelar</button>
              <button onClick={ejecutarLimpiarPeriodo} className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-black text-xs shadow-lg shadow-red-500/30 cursor-pointer">Sí, Limpiar Periodo</button>
            </div>
          </div>
        </div>
      )}

      {modalAuditoria.isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 transition-all duration-300">
          <div className="bg-white rounded-[28px] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.25)] w-full max-w-md border border-gray-100 animate-fade-in flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h3 className="text-xl font-black text-gray-800 flex items-center gap-2">🔍 Auditar Factura</h3>
              <button onClick={() => setModalAuditoria({isOpen: false, item: null, estado: 'PENDIENTE', nuevaObservacion: ''})} className="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-500 rounded-full hover:bg-red-100 hover:text-red-600 font-bold transition-colors">✕</button>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-4 mb-4 border border-slate-200 shadow-inner flex-shrink-0">
              <p className="text-sm text-gray-900 mb-1.5 font-black uppercase tracking-tight line-clamp-1">{modalAuditoria.item?.nombre}</p>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-gray-600 font-bold bg-white px-2 py-1 rounded-md border border-gray-100">DIAN: {modalAuditoria.item?.folioDian.startsWith('NO-DIAN') ? 'N/A' : modalAuditoria.item?.folioDian}</span>
                <span className="text-[10px] text-gray-600 font-bold bg-white px-2 py-1 rounded-md border border-gray-100">SYSPLUS: {modalAuditoria.item?.folioSysplus}</span>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 custom-scrollbar pr-2 mb-4">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-2 ml-1">Estado de Revisión</label>
              
              <select 
                value={modalAuditoria.estado} 
                onChange={(e) => setModalAuditoria({...modalAuditoria, estado: e.target.value})} 
                className={`w-full px-4 py-3 border-2 rounded-xl mb-4 text-xs font-black shadow-sm outline-none transition-all cursor-pointer ${
                modalAuditoria.estado === 'VALIDADO' ? 'bg-emerald-50 text-emerald-800 border-emerald-500' :
                 modalAuditoria.estado === 'EN_REVISION' ? 'bg-amber-50 text-amber-800 border-amber-500' :
                 modalAuditoria.estado === 'REPORTADA_RESPONSABLE' ? 'bg-purple-50 text-purple-800 border-purple-500' :
                'bg-gray-50 text-gray-600 border-gray-300'
                 }`}
                  >
                <option value="PENDIENTE" className="bg-white text-gray-700 font-bold">⏳ PENDIENTE</option>
                <option value="EN_REVISION" className="bg-amber-50 text-amber-900 font-bold">👀 EN REVISIÓN (Para revisar después)</option>
                <option value="VALIDADO" className="bg-emerald-50 text-emerald-900 font-bold">✅ VALIDADO (Todo OK)</option>
                <option value="REPORTADA_RESPONSABLE" className="bg-purple-50 text-purple-900 font-bold">📌 REPORTADA A RESPONSABLE</option>
                </select>

              {modalAuditoria.item?.observacion && (
                <div className="mb-4">
                  <label className="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-2 ml-1">Historial de Novedades</label>
                  <div className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl shadow-inner overflow-y-auto max-h-40 flex flex-col gap-2 custom-scrollbar">
                    {modalAuditoria.item.observacion.split('\n\n').filter(Boolean).map((bloque, i) => {
                      const esNuevo = bloque.startsWith('🔵');
                      const textoLimpio = bloque.replace('🔵 ', '');
                      return (
                        <div key={i} className={`p-2.5 rounded-xl border text-[11px] font-medium shadow-sm ${esNuevo ? 'bg-blue-50 border-blue-200 text-blue-900' : 'bg-white border-gray-200 text-gray-700'}`}>
                          <span className="whitespace-pre-wrap">{textoLimpio}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <label className="block text-[10px] font-black text-blue-600 uppercase tracking-wider mb-2 ml-1">➕ Agregar Nueva Novedad</label>
              <textarea 
                value={modalAuditoria.nuevaObservacion} 
                onChange={(e) => setModalAuditoria({...modalAuditoria, nuevaObservacion: e.target.value})} 
                className="w-full px-4 py-3 bg-blue-50/30 border border-blue-200 rounded-xl text-xs font-medium text-gray-800 shadow-inner outline-none focus:border-blue-500 focus:bg-white resize-none h-20 transition-all" 
                placeholder="Escribe aquí si hay alguna novedad..."
              />
            </div>

            <button onClick={guardarAuditoriaFactura} className="w-full flex-shrink-0 py-3.5 bg-[#007aff] hover:bg-[#005bb5] text-white font-black uppercase tracking-wider rounded-xl shadow-[0_8px_20px_rgba(0,122,255,0.3)] transition-all text-xs mt-2">
              Guardar Auditoría
            </button>
          </div>
        </div>
      )}

      <div className="bg-white/60 backdrop-blur-[35px] rounded-[34px] shadow-[0_25px_70px_rgba(0,0,0,0.12)] p-6 sm:p-10 max-w-7xl w-full mx-auto max-h-[92vh] overflow-y-auto border border-white/80 relative animate-fade-in">
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-gray-950/10 pb-6 mb-8 gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-white/80 border border-white flex items-center justify-center text-2xl shadow-sm text-blue-600">📅</div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">Cierre de Mes V2</h2>
                <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider ${estadoCierreV2 === 'CERRADO' ? 'bg-red-500/10 text-red-800' : 'bg-emerald-500/10 text-emerald-800'}`}>{estadoCierreV2.replace('_', ' ')}</span>
              </div>
              <p className="text-sm text-gray-600 font-medium mt-0.5">Auditoría DIAN, conciliación contable y bitácora de cierre.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {estadoCierreV2 === "ABIERTO" && facturasGuardadas.length > 0 && (
              <button onClick={() => setModalConfirmarLimpiar(true)} className="px-3.5 py-2.5 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold transition-all border border-red-200 shadow-sm cursor-pointer">
                🗑️ Limpiar Periodo
              </button>
            )}

            {estadoCierreV2 === "CERRADO" && (
              <button onClick={exportarExcelCierre} className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-xs font-bold transition-all shadow-md flex items-center gap-2 cursor-pointer">📊 Exportar Final</button>
            )}
            {fechaInicial && (
              <select value={estadoCierreV2} onChange={(e) => actualizarEstadoCierreV2(e.target.value)} disabled={cargandoCierre} className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-800 shadow-sm cursor-pointer outline-none">
                <option value="ABIERTO">🟢 ABIERTO</option><option value="EN_VALIDACION">🟡 EN VALIDACIÓN</option>
                <option value="CON_ALERTAS">🟠 CON ALERTAS</option><option value="EN_REVISION">🟣 EN REVISION</option>
                <option value="CERRADO">🔒 CERRADO</option>
              </select>
            )}
            <button onClick={onBackToMenu} className="px-5 py-2.5 rounded-xl bg-white/70 hover:bg-white text-gray-800 text-xs font-bold border border-white shadow-sm cursor-pointer">Volver al Menú</button>
          </div>
        </div>

        {mensaje.text && (
          <div className={`mb-6 p-4 rounded-2xl text-xs sm:text-sm font-bold flex justify-between items-center backdrop-blur-md ${mensaje.type === "success" ? "bg-emerald-500/10 text-emerald-900" : mensaje.type === "warning" ? "bg-amber-500/10 text-amber-900" : "bg-red-500/10 text-red-900"}`}>
            <span>{mensaje.text}</span><button onClick={() => setMensaje({ text: "", type: "" })} className="text-xs font-black">✕</button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white/40 backdrop-blur-md p-6 rounded-3xl border border-white/70 shadow-sm flex flex-col justify-between">
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-950/10">
              <h3 className="text-xs font-extrabold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                <span className="w-1.5 h-4 bg-blue-600 rounded-full"></span>1. Rango y Extracción Sysplus
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Inicio *</label>
                <input type="date" value={fechaInicial} onChange={(e) => { setFechaInicial(e.target.value); setSysplusExtraido(false); setComprasSysplusCache([]); }} className="w-full px-3.5 py-2.5 bg-white/70 border border-white/90 rounded-2xl text-gray-900 text-xs focus:outline-none shadow-inner font-medium cursor-pointer" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Fin *</label>
                <input type="date" value={fechaFinal} onChange={(e) => { setFechaFinal(e.target.value); setSysplusExtraido(false); setComprasSysplusCache([]); }} className="w-full px-3.5 py-2.5 bg-white/70 border border-white/90 rounded-2xl text-gray-900 text-xs focus:outline-none shadow-inner font-medium cursor-pointer" />
              </div>
            </div>
            
            {tienePermisoSysplus && (
              <button 
                onClick={handleExtraerSysplusPrimero} 
                disabled={!fechasLlenas || procesando} 
                className={`w-full py-3 rounded-2xl font-black text-xs uppercase tracking-wider transition-all shadow-md cursor-pointer flex items-center justify-center gap-2 ${
                  sysplusExtraido ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-[#007aff] hover:bg-[#0062cc] text-white'
                } disabled:opacity-50`}
              >
                {sysplusExtraido ? "✅ Sysplus Extraído (Listo para DIAN)" : "📥 1. Extraer Datos de Sysplus"}
              </button>
            )}
          </div>

          <div className={`p-6 rounded-3xl border-2 border-dashed text-center flex flex-col justify-center transition backdrop-blur-md ${
            sysplusExtraido && estadoCierreV2 !== 'CERRADO' ? "bg-white/60 border-blue-400 shadow-md" : "bg-gray-100/50 border-gray-300 opacity-60"
          }`}>
            <h3 className="text-xs font-extrabold mb-2 uppercase tracking-wider text-gray-800">2. Subir Archivo DIAN y Ejecutar Cruce</h3>
            <p className="text-[10px] text-gray-500 mb-3">
              {sysplusExtraido ? "✨ Sysplus listo. Selecciona tu Excel DIAN para comparar." : "🔒 Primero completa el Paso 1 (Extraer Sysplus)."}
            </p>
            {tienePermisoDian && estadoCierreV2 !== 'CERRADO' ? (
              <input 
                type="file" 
                accept=".xlsx, .xls, .csv" 
                onChange={handleCargarDian} 
                disabled={!sysplusExtraido || procesando} 
                className={`block w-full max-w-sm mx-auto text-xs file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:font-bold file:text-white transition ${
                  sysplusExtraido ? "text-gray-700 file:bg-emerald-600 cursor-pointer" : "text-gray-400 file:bg-gray-400 cursor-not-allowed"
                }`} 
              />
            ) : (
              <p className="text-[10px] text-gray-500 mt-1 font-medium">Bloqueado o sin permisos.</p>
            )}
          </div>
        </div>

        {procesando ? (
          <div className="text-center py-12 bg-white/40 backdrop-blur-md rounded-3xl border border-white/70 shadow-sm"><span className="text-4xl animate-spin block mb-3">⚙</span><p className="text-xs font-bold text-gray-700">Procesando y cruzando datos...</p></div>
        ) : fechaInicial && (
          <div className="space-y-6 animate-fade-in">
            
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 bg-white/40 backdrop-blur-xl p-4 rounded-3xl border border-white/70 shadow-sm">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-[10px] font-extrabold text-gray-600 uppercase tracking-wider mb-1.5">Buscar Proveedor, NIT o Folio</label>
                <input type="text" placeholder="Ej. Postobon..." value={busqueda} onChange={(e) => {setBusqueda(e.target.value); setPaginaActual(1);}} className="w-full px-4 py-2.5 rounded-2xl border border-white/90 bg-white/70 text-xs shadow-inner focus:outline-none" />
              </div>

              <div className="flex items-center gap-2 self-end sm:self-auto mb-1">
                <button onClick={() => setVistaActual("CRUCE")} className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition ${vistaActual === "CRUCE" ? "bg-slate-900 text-white shadow-sm" : "bg-white/70 text-gray-700 hover:bg-white"}`}>Cruce</button>
                <button onClick={() => setVistaActual("BITACORA")} className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition ${vistaActual === "BITACORA" ? "bg-blue-600 text-white shadow-sm" : "bg-white/70 text-gray-700 hover:bg-white"}`}>Bitácora</button>
              </div>
              
              {vistaActual === "CRUCE" && (
              <>
              <div className="relative w-full sm:w-44 animate-fade-in" onMouseLeave={() => setMostrarMenuTipos(false)}>
                <label className="block text-[10px] font-extrabold text-gray-600 uppercase tracking-wider mb-1.5">Filtro Tipo</label>
                
                <div 
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 bg-white text-[11px] shadow-sm cursor-pointer font-bold flex justify-between items-center hover:bg-gray-50 transition-colors"
                  onClick={() => setMostrarMenuTipos(!mostrarMenuTipos)}
                >
                  <span className="truncate text-gray-700">
                    {filtroTipos.length === 3 ? "Todos (FE, NC, ND)" : filtroTipos.length === 0 ? "Seleccionar..." : filtroTipos.join(", ")}
                  </span>
                  <span className="text-[9px] text-gray-400">{mostrarMenuTipos ? '▲' : '▼'}</span>
                </div>

                {mostrarMenuTipos && (
                  <div className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-lg border border-gray-200 py-1.5 flex flex-col">
                    {[
                      { id: "FE", label: "Factura (FE)" },
                      { id: "NC", label: "Nota Crédito (NC)" },
                      { id: "ND", label: "Nota Débito (ND)" }
                    ].map(opcion => (
                      <label key={opcion.id} className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-blue-50 cursor-pointer transition-colors group">
                        <input 
                          type="checkbox" 
                          checked={filtroTipos.includes(opcion.id)}
                          onChange={() => {
                            setFiltroTipos(prev => prev.includes(opcion.id) ? prev.filter(t => t !== opcion.id) : [...prev, opcion.id]);
                            setPaginaActual(1);
                          }}
                          className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                        />
                        <span className="text-[11px] font-bold text-gray-700 group-hover:text-blue-700">{opcion.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

                  <div className="w-full sm:w-44 animate-fade-in">
                    <label className="block text-[10px] font-extrabold text-gray-600 uppercase tracking-wider mb-1.5">Orden Proveedor</label>
                    <select 
                      value={sortCruce.key === 'nombre' ? sortCruce.direction : 'default'} 
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === 'default') setSortCruce({ key: null, direction: 'asc' });
                        else setSortCruce({ key: 'nombre', direction: val });
                        setPaginaActual(1);
                      }} 
                      className="w-full px-4 py-2.5 rounded-2xl border border-white/90 bg-white/70 text-xs shadow-inner focus:outline-none font-bold cursor-pointer"
                    >
                      <option value="default">Por defecto</option>
                      <option value="asc">A - Z (Ascendente)</option>
                      <option value="desc">Z - A (Descendente)</option>
                    </select>
                  </div>
                </>
              )}
            </div>

            {vistaActual === "BITACORA" && (
              <div className="bg-white/40 backdrop-blur-xl rounded-3xl border border-white/70 p-6 shadow-sm animate-fade-in">
                <h3 className="text-sm font-extrabold text-gray-800 flex items-center gap-2 mb-6">📝 Bitácora de Cierre y Comentarios</h3>
                <div className="flex gap-3 mb-6">
                  <input type="text" value={nuevoComentario} onChange={(e) => setNuevoComentario(e.target.value)} placeholder={estadoCierreV2 === "CERRADO" ? "Comentarios bloqueados." : "Escribe una observación..."} className="flex-1 px-4 py-2.5 rounded-2xl bg-white/70 text-xs shadow-inner focus:outline-none disabled:bg-gray-100" disabled={estadoCierreV2 === "CERRADO"} />
                  <button onClick={handleAgregarComentario} disabled={!nuevoComentario.trim() || estadoCierreV2 === "CERRADO"} className="px-6 py-2.5 bg-blue-600 text-white text-xs font-bold rounded-2xl shadow-sm disabled:opacity-50">Publicar</button>
                </div>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {cargandoHistorial ? <p className="text-xs text-gray-500 text-center py-4">Cargando...</p> : historial.length === 0 ? <p className="text-xs text-gray-500 italic text-center py-4">No hay registros.</p> : historial.map((reg) => (
                    <div key={reg.id} className="p-3.5 bg-white/80 rounded-2xl border border-gray-100 shadow-sm text-xs flex flex-col">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-black text-[10px] shrink-0">
                            {reg.usuario?.charAt(0).toUpperCase()}
                          </span>
                          <span className="font-extrabold text-gray-900 leading-tight">
                            {reg.usuario}
                          </span>
                        </div>
                        <span className="text-[10px] font-bold text-gray-400 shrink-0 ml-2 mt-0.5">{new Date(reg.fecha).toLocaleString('es-CO')}</span>
                      </div>
                      <p className="text-gray-700 font-medium pl-8 bg-gray-50/50 p-2 rounded-lg ml-8 mt-1 border border-gray-100/50">
                        {reg.tipo_accion === 'CAMBIO_ESTADO' ? '🔄 ' : reg.tipo_accion === 'COMENTARIO' ? '💬 ' : '📊 '}{reg.detalle}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {vistaActual === "CRUCE" && tienePermisoAuditoria && (
              <>
                {facturasGuardadas.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-slate-900/80 p-4 rounded-3xl shadow-sm text-white"><span className="text-[10px] font-bold uppercase text-slate-400">Neto DIAN</span><p className="text-lg font-black mt-1">${kpis.netoDian.toLocaleString("es-CO", { maximumFractionDigits: 0 })}</p></div>
                      <div className="bg-white/60 p-4 rounded-3xl shadow-sm border border-white/80"><span className="text-[10px] font-bold uppercase text-gray-500">Neto Sysplus</span><p className="text-lg font-black mt-1 text-gray-900">${kpis.netoSysplus.toLocaleString("es-CO", { maximumFractionDigits: 0 })}</p></div>
                      <div className="bg-red-500/10 p-4 rounded-3xl shadow-sm border border-red-500/20"><span className="text-[10px] font-bold uppercase text-red-700">Diferencia Neta</span><p className="text-lg font-black mt-1 text-red-800">${Math.abs(kpis.netoDian - kpis.netoSysplus).toLocaleString("es-CO", { maximumFractionDigits: 0 })}</p></div>
                      <div className="bg-emerald-500/10 p-4 rounded-3xl shadow-sm border border-emerald-500/20"><span className="text-[10px] font-bold uppercase text-emerald-700">Validados (✅)</span><p className="text-lg font-black mt-1 text-emerald-800">{kpis.validados} de {kpis.total}</p></div>
                    </div>

                    <div className="bg-white/40 rounded-3xl border border-white/70 p-5 shadow-sm">
                      
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                        <div className="flex flex-wrap gap-2">
                          <button onClick={() => {setFiltroEstado("TODOS"); setPaginaActual(1);}} className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold border shadow-sm transition cursor-pointer ${filtroEstado === "TODOS" ? "bg-slate-900 text-white border-slate-900" : "bg-white/80 text-gray-700 hover:bg-white"}`}>Todos ({kpis.total})</button>
                          <button onClick={() => {setFiltroEstado("CONCILIADO"); setPaginaActual(1);}} className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold border shadow-sm transition cursor-pointer ${filtroEstado === "CONCILIADO" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white/80 text-emerald-800 hover:bg-emerald-50"}`}>✓ Conciliados ({kpis.ok})</button>
                          <button onClick={() => {setFiltroEstado("DIFERENCIA"); setPaginaActual(1);}} className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold border shadow-sm transition cursor-pointer ${filtroEstado === "DIFERENCIA" ? "bg-amber-500 text-white border-amber-500" : "bg-white/80 text-amber-800 hover:bg-amber-50"}`}>± Dif. Valores ({kpis.dif})</button>
                          <button onClick={() => {setFiltroEstado("FALTANTE_SISTEMA"); setPaginaActual(1);}} className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold border shadow-sm transition cursor-pointer ${filtroEstado === "FALTANTE_SISTEMA" ? "bg-red-600 text-white border-red-600" : "bg-white/80 text-red-800 hover:bg-red-50"}`}>⚠ Falta en Sysplus ({kpis.faltaSist})</button>
                          <button onClick={() => {setFiltroEstado("FALTANTE_DIAN"); setPaginaActual(1);}} className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold border shadow-sm transition cursor-pointer ${filtroEstado === "FALTANTE_DIAN" ? "bg-purple-600 text-white border-purple-600" : "bg-white/80 text-purple-800 hover:bg-purple-50"}`}>⚠ Falta en DIAN ({kpis.faltaDian})</button>
                        </div>

                        <div className="flex gap-2">
                          {estadoCierreV2 !== "CERRADO" && kpis.faltaSist > 0 && (
                            <button onClick={handleRefrescarFaltantes} className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-[11px] font-black uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center gap-1.5 cursor-pointer">
                              🔄 Refrescar Faltantes
                            </button>
                          )}
                          
                          {estadoCierreV2 !== "CERRADO" && (
                            <button onClick={validarConciliadosMasivamente} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-black uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center gap-1.5 cursor-pointer">
                              ✓ Validar Conciliados
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="overflow-x-auto max-h-[60vh] border border-white/80 rounded-2xl relative bg-white/40 shadow-inner">
                        <table className="w-full text-left text-[11px] text-gray-800">
                          <thead className="bg-white/90 text-gray-900 font-extrabold sticky top-0 border-b border-gray-950/10 shadow-sm z-10">
                            <tr>
                              <th className="p-3.5 cursor-pointer whitespace-nowrap hover:bg-gray-100 transition-colors" onClick={() => handleSortCruce('estado_auditoria')}>Auditoría {renderSortIcon('estado_auditoria')}</th>
                              <th className="p-3.5 cursor-pointer whitespace-nowrap hover:bg-gray-100 transition-colors" onClick={() => handleSortCruce('estado')}>Estado Cruce {renderSortIcon('estado')}</th>
                              <th className="p-3.5 cursor-pointer whitespace-nowrap hover:bg-gray-100 transition-colors" onClick={() => handleSortCruce('folioDian')}>Folio DIAN / Fecha Generación {renderSortIcon('folioDian')}</th>
                              <th className="p-3.5 cursor-pointer whitespace-nowrap hover:bg-gray-100 transition-colors" onClick={() => handleSortCruce('folioSysplus')}>Folio Sysplus / Fecha Sist. {renderSortIcon('folioSysplus')}</th>
                              <th className="p-3.5 cursor-pointer min-w-[200px] hover:bg-gray-100 transition-colors" onClick={() => handleSortCruce('nombre')}>Proveedor / NIT {renderSortIcon('nombre')}</th>
                              <th className="p-3.5 text-right cursor-pointer whitespace-nowrap hover:bg-gray-100 transition-colors" onClick={() => handleSortCruce('totalDian')}>Total DIAN {renderSortIcon('totalDian')}</th>
                              <th className="p-3.5 text-right cursor-pointer whitespace-nowrap hover:bg-gray-100 transition-colors" onClick={() => handleSortCruce('totalSysplus')}>Total Sysplus {renderSortIcon('totalSysplus')}</th>
                              <th className="p-3.5 text-right cursor-pointer whitespace-nowrap hover:bg-gray-100 transition-colors" onClick={() => handleSortCruce('diferencia')}>Diferencia {renderSortIcon('diferencia')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200/50">
                            {datosFiltrados.length === 0 ? (
                              <tr>
                                <td colSpan="8" className="p-10 text-center text-gray-500 font-bold bg-white/50">
                                  No hay facturas que coincidan con los filtros seleccionados.
                                </td>
                              </tr>
                            ) : (
                              datosPaginados.map((item) => {
                                const tDoc = formatTipoDoc(item.tipo);
                                
                                const tieneNotas = item.observacion && item.observacion.trim() !== "";
                                const esValidadoConNotas = item.estado_auditoria === 'VALIDADO' && tieneNotas;
                                
                                let btnClase = 'bg-gray-100 text-gray-600 hover:bg-gray-200';
                                let btnTexto = '⏳ Pendiente';
                                let rowClase = 'hover:bg-gray-50 transition border-b border-gray-100/50';
                                
                                if (esValidadoConNotas) {
                                  btnClase = 'bg-emerald-100 text-emerald-800 border border-emerald-500 border-dashed hover:bg-emerald-200';
                                  btnTexto = '✅ Validado (Notas)';
                                  rowClase = 'bg-emerald-100 hover:bg-emerald-200 transition border-b border-emerald-300'; 
                                } else if (item.estado_auditoria === 'VALIDADO') {
                                  btnClase = 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm';
                                  btnTexto = '✅ Validado';
                                  rowClase = 'bg-emerald-200 hover:bg-emerald-300 transition border-b border-emerald-400'; 
                                } else if (item.estado_auditoria === 'EN_REVISION') {
                                  btnClase = 'bg-amber-400 text-amber-900 hover:bg-amber-500 shadow-sm';
                                  btnTexto = '👀 Revisar';
                                  rowClase = 'bg-amber-100 hover:bg-amber-200 transition border-b border-amber-300'; 
                                } else if (item.estado_auditoria === 'REPORTADA_RESPONSABLE') {
                                  btnClase = 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm';
                                  btnTexto = '📌 Reportada';
                                  rowClase = 'bg-purple-100 hover:bg-purple-200 transition border-b border-purple-300'; 
                                } 
                                
                                return (
                                  <tr key={item.id} className={rowClase}>
                                    <td className="p-3.5 align-top min-w-[120px]">
                                      <button 
                                        onClick={() => setModalAuditoria({ isOpen: true, item, estado: item.estado_auditoria || 'PENDIENTE', nuevaObservacion: '' })}
                                        disabled={estadoCierreV2 === "CERRADO"} 
                                        className={`px-3 py-1.5 rounded-full font-bold transition-all text-[9px] uppercase tracking-wider flex items-center justify-center gap-1 w-full max-w-[115px] cursor-pointer ${btnClase}`}
                                      >
                                        {btnTexto}
                                      </button>
                                      {item.observacion && (
                                        <p className="text-[9px] text-gray-500 mt-1.5 line-clamp-2 leading-tight" title={item.observacion}>
                                          <span className="font-bold text-gray-700">Ver Notas ({item.observacion.split('🔵').length - 1 || 1})</span>
                                        </p>
                                      )}
                                    </td>
                                    <td className="p-3.5 align-top">
                                      <span className={`inline-block px-2.5 py-1 rounded-full font-bold whitespace-nowrap text-[10px] border ${
                                        item.estado === "CONCILIADO" ? "bg-emerald-500/10 text-emerald-800 border-emerald-500/20" :
                                        item.estado === "DIFERENCIA" ? "bg-amber-500/20 text-amber-900 border-amber-500/30" :
                                        item.estado === "FALTANTE_SISTEMA" ? "bg-red-500/10 text-red-800 border-red-500/20" :
                                        "bg-purple-500/10 text-purple-900 border-purple-500/20"
                                      }`}>
                                        {item.estado === "CONCILIADO" && "✓ Conciliado"}
                                        {item.estado === "DIFERENCIA" && "± Dif. Valor"}
                                        {item.estado === "FALTANTE_SISTEMA" && "Falta Sysplus"}
                                        {item.estado === "FALTANTE_DIAN" && "Falta DIAN"}
                                      </span>
                                    </td>
                                    
                                    <td className="p-3.5 font-mono text-gray-700 font-bold whitespace-nowrap align-top">
                                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border mb-1 inline-block ${tDoc.color}`}>{tDoc.label}</span>
                                      <br/>
                                      {item.folioDian.startsWith('NO-DIAN') ? <span className="text-gray-400 italic">No en DIAN</span> : item.folioDian}
                                      {item.fechaDian && <p className="text-[10px] text-gray-500 font-normal mt-0.5">📅 Generado: {String(item.fechaDian).split('T')[0]}</p>}
                                    </td>
                                    
                                    <td className="p-3.5 font-mono text-blue-600 font-bold whitespace-nowrap align-top">
                                      {item.folioSysplus}
                                      {item.fechaSysplus && <p className="text-[10px] text-blue-400 font-normal mt-0.5">📅 Fecha Sist: {String(item.fechaSysplus).split('T')[0]}</p>}
                                    </td>
                                    
                                    <td className="p-3.5 align-top"><p className="font-extrabold text-gray-900 leading-tight">{item.nombre}</p><p className="text-[10px] text-gray-500 font-medium">NIT: {item.nit}</p></td>
                                    <td className="p-3.5 text-right font-semibold text-gray-700 whitespace-nowrap align-top">{item.totalDian === 0 ? "-" : `$${item.totalDian.toLocaleString("es-CO")}`}</td>
                                    <td className="p-3.5 text-right font-semibold text-gray-700 whitespace-nowrap align-top">{item.totalSysplus === 0 ? "-" : `$${item.totalSysplus.toLocaleString("es-CO")}`}</td>
                                    <td className={`p-3.5 text-right font-black whitespace-nowrap align-top ${item.diferencia < 50 ? "text-emerald-600" : "text-red-600"}`}>${item.diferencia.toLocaleString("es-CO")}</td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>

                      {totalPaginas > 1 && (
                        <div className="flex justify-between items-center mt-4 bg-white/60 p-3 rounded-2xl border border-white/80 shadow-sm">
                          <span className="text-[10px] font-bold text-gray-500">Mostrando {((paginaActual - 1) * itemsPorPagina) + 1} - {Math.min(paginaActual * itemsPorPagina, datosFiltrados.length)} de {datosFiltrados.length} facturas</span>
                          <div className="flex gap-2">
                            <button onClick={() => setPaginaActual(p => Math.max(1, p - 1))} disabled={paginaActual === 1} className="px-4 py-2 rounded-xl text-xs font-bold bg-white border border-gray-200 shadow-sm disabled:opacity-50 transition-all hover:bg-gray-50">Anterior</button>
                            <div className="flex items-center px-3 font-bold text-xs text-gray-700">Pág. {paginaActual} de {totalPaginas}</div>
                            <button onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))} disabled={paginaActual === totalPaginas} className="px-4 py-2 rounded-xl text-xs font-bold bg-white border border-gray-200 shadow-sm disabled:opacity-50 transition-all hover:bg-gray-50">Siguiente</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="bg-white/40 rounded-3xl border border-dashed border-gray-300 p-12 text-center shadow-sm">
                    <span className="text-5xl block mb-4">📂</span><h3 className="text-lg font-black text-gray-800 mb-2">Aún no hay facturas en este periodo</h3>
                    <p className="text-xs text-gray-500 max-w-md mx-auto font-medium">Extrae primero los datos de Sysplus (Paso 1) y luego sube tu Excel DIAN (Paso 2) para realizar el cruce.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}